import CameraStreamConnection from './CameraStreamConnection';
import { CameraStreamPlayer } from './CameraStreamPlayer';
import { TRANSACTION_PAGE_TABS } from 'config';
import { kurentoClientManager } from './KurentoClient/KurentoClientManager';
import VideoOutputManager from './VideoOutputManager';
import StreamStateManager from './StreamStateManager';
import EventDispatcher from './EventDispatcher';

import { axiosAiq } from 'config';
import { notificationHandlerSync } from 'utils/utils';
import { ACTION_ARGS, ACTION_NAMES } from './ActionScheduler/configuration';
import ActionScheduler from './ActionScheduler/ActionScheduler';
import { isCameraStreamLogsEnabled, STREAM_TYPE } from '../config';
import { webRtcTelemetry } from 'services/StreamingObservability/WebRtcTelemetryService';

const videoWallTransactioId = TRANSACTION_PAGE_TABS.VIDEO_WALL_TAB.DUMMY_TRANSACTION_ID;

//TODO KurentoRtspSource has many responsibilites.Further separation of concerns is needed (when there is a time)
export default class KurentoRtspSource {
    aot;
    kurentoId;
    cameraId;

    #playerIdErrors = new Map();

    #cameraStreamConnection;
    #cameraStreamPlayerByStreamType = new Map();

    #kurentoIdsCoolOffBench = new Map();
    #kurentoIdCoolOffMillis = 15000;

    #streamRefreshCoolOffMillis = 15000;
    #lastRefreshTime = Date.now() - this.#streamRefreshCoolOffMillis;

    #activeMediaPiplineId;
    activeStreamType = '';
    #activateOnUpdateStreamType = '';
    #latestCamConfig;

    activatingStreamType = '';

    #mediaStream;

    #videoOutputManager;
    streamStateManager;
    #eventDispatcher;
    #forceStop;

    #fullHeader;
    #playerEndpointIdErrorTimeoutId;
    #updatingConfigurationPromise;
    #updatingConfiguration;
    #isStreamConfigurationInvalidated;

    constructor(kurentoId, cameraId) {
        this.kurentoId = kurentoId;
        this.cameraId = cameraId;

        this.streamStateManager = new StreamStateManager(this.#log.bind(this));
        this.actionScheduler = new ActionScheduler(this);
        this.#videoOutputManager = new VideoOutputManager(this.#log.bind(this), cameraId);
        this.#eventDispatcher = new EventDispatcher(
            this.#log.bind(this),
            this.#logError.bind(this),
            this.#videoOutputManager,
            this.streamStateManager
        );
        this.streamStateManager.setEventDispatcher(this.#eventDispatcher);

        window.taskScheduler = this.actionScheduler;
        this.#setLogHeaders();
    }

    schedule(action, ...args) {
        this.actionScheduler.schedule(action, args);
    }

    getPeerConnection() {
        return this.#cameraStreamConnection?.getPeerConnection?.() || null;
    }

    // AIQ site of the camera, read by the NACK stats log
    getSiteName() {
        return this.#latestCamConfig?.locationName;
    }

    onFreezeDetectorVerdict(frozen, reason) {
        this.#recordTelemetry(
            {
                eventType: frozen ? 'video_frozen' : 'video_active',
                frozen,
                failureReason: frozen ? 'frozen_video_detected' : undefined,
                outcome: frozen ? 'freeze_detected' : 'frames_advancing',
            },
            { peerConnection: this.getPeerConnection() }
        );
        if (frozen) {
            this.#log(`FreezeDetector verdict: FROZEN (${reason || ''})`);
            this.#enterFrozenState();
        } else {
            this.#log(`FreezeDetector verdict: ACTIVE (${reason || ''})`);
            this.#exitFrozenState();
        }
    }

    scheduleAndWait(action, ...args) {
        return this.actionScheduler.scheduleAndWait(action, args);
    }

    initiateKurentoClient = (changingKurentoServer) => {
        if (!this.kurentoId) {
            this.streamStateManager.setError('KurentoId not provided');
            return;
        }
        if (kurentoClientManager.isClientCreated(this.kurentoId)) {
            this.actionScheduler.start();
            return;
        }
        this.streamStateManager.setInfo(
            `${changingKurentoServer ? 'Changing kurento server. ' : ''}Waiting for kurento id=${this.kurentoId} connection...`
        );
        const kurentoClient = kurentoClientManager.get(this.kurentoId);
        kurentoClient.then(this.actionScheduler.start).catch((err) => this.#handle(err, 'initiateKurentoClient()', false));
    };

    //TODO export actions business logic to action classes (when there is a time)
    async [ACTION_NAMES.CONFIGURE_CAMERA_STREAM](camConfig, updateConfig, predefining) {
        try {
            this.#latestCamConfig = camConfig;
            //if block bellow intentionaly is not in separate fn
            if (this.#updatingConfiguration) {
                await this.#updatingConfigurationPromise;
                this.#log(`${ACTION_NAMES.CONFIGURE_CAMERA_STREAM}():awaited cameraStreamConfiguration update to finish`);
            }

            if (!this.#shouldUpdateCameraStream(camConfig)) return;

            const streamType = camConfig?.videoData?.streamType;

            if (this.activeStreamType === streamType) this.streamStateManager.updateState({ info: '', error: '', freezed: false, isLoading: false });
            else this.streamStateManager.reset();

            if (updateConfig) {
                this.#log(`${ACTION_NAMES.CONFIGURE_CAMERA_STREAM}(): Initiating cameraStreamConfig update streamType=${streamType}`);
                await (this.#updatingConfigurationPromise = this.#updateStreamConfig(camConfig));
                return;
            }

            this.#addNewStream(camConfig);
            if (predefining) {
                const promises = [];
                const connectionPromise = this.#cameraStreamConnection?.connectedPromise;
                const playerEndpointPromise = this.playerByStreamType(streamType)?.activationPromise;
                if (connectionPromise) promises.push(connectionPromise);
                if (playerEndpointPromise) promises.push(playerEndpointPromise);
                await Promise.all(promises);
                this.streamStateManager.reset();
                this.streamStateManager.setInfo('Activating...');
            }
        } catch (err) {
            this.#handle(err, ACTION_NAMES.CONFIGURE_CAMERA_STREAM, false);
        }
    }

    #reactivateStream = () => {
        const streamType = this.activeStreamType;
        if (streamType) {
            this.#log(`reactivateStream(): Scheduling reactivation of stream type=${streamType}`);
            this.schedule(ACTION_NAMES.ACTIVATE_CAMERA_STREAM_TYPE, streamType, true);
        } else {
            this.#log(`reactivateStream(): No active stream type, exiting.`);
        }
    };

    async [ACTION_NAMES.ACTIVATE_CAMERA_STREAM_TYPE](streamType, forceActivation) {
        try {
            if (this.#isStreamConfigurationInvalidated) {
                this.#handleStreamConfigurationInvalidated(ACTION_NAMES.ACTIVATE_CAMERA_STREAM_TYPE);
                return;
            }

            if (this.#updatingConfiguration && forceActivation !== 'updateConfig') {
                await this.#updatingConfigurationPromise;
                this.#log(`${ACTION_NAMES.ACTIVATE_CAMERA_STREAM_TYPE}():awaited cameraStreamConfiguration to finish`);
            }

            if (!streamType || ((this.activeStreamType === streamType || this.activatingStreamType === streamType) && !forceActivation)) {
                this.#log(`${ACTION_NAMES.ACTIVATE_CAMERA_STREAM_TYPE}(): exiting without changing streamType`);
                this.#activateOnUpdateStreamType = '';
                return;
            }

            this.activatingStreamType = streamType;

            let oppositeStreamType;

            const currentPlayer = this.playerByStreamType(this.activeStreamType);
            let playerToActivate = this.playerByStreamType(streamType);
            const connection = this.#cameraStreamConnection;

            ({ oppositeStreamType, playerToActivate } = this.#getPlayerToActivate(streamType, playerToActivate, connection) || {});

            if (!playerToActivate) {
                this.activatingStreamType = '';
                this.streamStateManager.updateState({ info: '', error: 'No player available, try hard refresh', isLoading: false });
                return;
            }

            if (!connection) {
                this.#log(`${ACTION_NAMES.ACTIVATE_CAMERA_STREAM_TYPE}(): No CameraStreamConnection | exiting`);
                this.activatingStreamType = '';
                throw new Error('No CameraStreamConnection object [#1]');
            }
            if (
                forceActivation === 'updateConfig' &&
                playerToActivate === currentPlayer &&
                playerToActivate.playerEndpointId.startsWith(this.#activeMediaPiplineId)
            ) {
                this.activatingStreamType = '';
                this.#log(`${ACTION_NAMES.ACTIVATE_CAMERA_STREAM_TYPE}(): already activated stream type | exiting`);
                return;
            }

            if (!playerToActivate.live || !connection.live) {
                await Promise.all([playerToActivate.activationPromise, connection.activationPromise]);
                this.#log(
                    `${ACTION_NAMES.ACTIVATE_CAMERA_STREAM_TYPE}(): awaited for Player or Connection activation proces to finish before proceeding`
                );
            }

            const activatedStreamType = oppositeStreamType || streamType;
            await this.#connectCameraStreamPlayerToConnection(currentPlayer, playerToActivate, connection, activatedStreamType);

            if (oppositeStreamType) {
                this.#log(`${ACTION_NAMES.ACTIVATE_CAMERA_STREAM_TYPE}():start listening for streamType=${streamType} configUpdate`);
                this.#activateOnUpdateStreamType = streamType;
            } else if (this.#activateOnUpdateStreamType) {
                this.#log(`${ACTION_NAMES.ACTIVATE_CAMERA_STREAM_TYPE}():stopping listening for configUpdate`);
                this.#activateOnUpdateStreamType = '';
            }

            this.activeStreamType = activatedStreamType;
            this.activatingStreamType = '';
            this.#log(`${ACTION_NAMES.ACTIVATE_CAMERA_STREAM_TYPE}(): activated streamType=${activatedStreamType}`);
        } catch (err) {
            this.activatingStreamType = '';
            this.#handle(err, `${ACTION_NAMES.ACTIVATE_CAMERA_STREAM_TYPE}()`, false);
        }
    }

    async [ACTION_NAMES.RELEASE_CAMERA_STREAM](forceStop) {
        try {
            this.streamStateManager.reset();
            this.#videoOutputManager.clearAllVideoOutputs();
            await this.#cameraStreamConnection?.release(forceStop);
            this.#log(`${ACTION_NAMES.RELEASE_CAMERA_STREAM}(): released CameraStreamConnection`);
            this.#cameraStreamConnection = null;
            this.#cameraStreamPlayerByStreamType = new Map();
            this.activeStreamType = '';
        } catch (err) {
            this.#handle(err, `${ACTION_NAMES.RELEASE_CAMERA_STREAM}()`, false);
        }
    }

    #isStreamRefreshAllowed() {
        const now = Date.now();
        const timeSinceLastRefresh = now - this.#lastRefreshTime;
        const isAllowed = timeSinceLastRefresh >= this.#streamRefreshCoolOffMillis;
        if (isAllowed) {
            this.#lastRefreshTime = now;
            return true;
        }
        this.#log(
            `#isStreamRefreshAllowed(): Refresh not allowed, coolOffPeriod=${this.#streamRefreshCoolOffMillis} miliseconds,  time elapsed since last stream refresh=${timeSinceLastRefresh} miliseconds`
        );
        return false;
    }

    invalidateStreamConfig() {
        this.#isStreamConfigurationInvalidated = true;
        this.#cameraStreamConnection?.setIsInvalidated(true);
        this.actionScheduler.schedule(
            ACTION_NAMES.CONFIGURE_CAMERA_STREAM,
            ACTION_ARGS[ACTION_NAMES.CONFIGURE_CAMERA_STREAM].INVALIDATE_STREAM_CONFIGURATION
        );
    }

    async [ACTION_NAMES.SOFT_REFRESH_CAMERA_STREAM](streamType) {
        try {
            if (this.#isStreamConfigurationInvalidated) {
                this.#handleStreamConfigurationInvalidated(ACTION_NAMES.SOFT_REFRESH_CAMERA_STREAM);
                return;
            }
            if (!this.#isStreamRefreshAllowed()) return;
            this.streamStateManager.updateState({ info: 'Soft refresh in progress...', error: '', freezed: false });
            if (this.#updatingConfiguration) {
                await this.#updatingConfigurationPromise;
                this.#log(`${ACTION_NAMES.SOFT_REFRESH_CAMERA_STREAM}(): awaited cameraStreamConfiguration update to finish`);
            }
            if (!this.#cameraStreamConnection) throw new Error('No CameraStreamConnection object [#2]');
            if (this.#cameraStreamConnection?.recreating) {
                this.#log('Already recreating exiting');
                return;
            }
            await this.#cameraStreamConnection.recreate();
            await this[ACTION_NAMES.ACTIVATE_CAMERA_STREAM_TYPE](streamType || this.activatingStreamType, true);
        } catch (err) {
            this.#handle(err, `${ACTION_NAMES.SOFT_REFRESH_CAMERA_STREAM}()`, false);
        }
    }

    async hardRefresh(forceRefreshMode) {
        try {
            if (!this.#isStreamRefreshAllowed()) return;
            this.#recordTelemetry({
                eventType: 'hard_refresh_attempted',
                hardRefreshAttempted: true,
                hardRefreshOutcome: 'requested',
            });

            const { info, error } = this.streamStateManager.getCurrentState() || {};
            const isStreaming = !info && !error;

            this.streamStateManager.updateState({ info: 'Requesting hard refresh, please wait...', error: '', freezed: false });

            const urlPath = `/camera-stream/camera/${this.cameraId}/refresh${forceRefreshMode ? `?kurentoId=${this.kurentoId}` : ''}`;
            const { data } = await axiosAiq.post(urlPath);
            this.#recordTelemetry({
                eventType: 'hard_refresh_request_accepted',
                hardRefreshAttempted: true,
                hardRefreshOutcome: 'request_accepted',
            });
            const [{ code }] = data || [{}];
            if (code === 'CAMERA_ALREADY_REFRESHED') {
                this.#recordTelemetry({
                    eventType: 'hard_refresh_already_queued',
                    hardRefreshAttempted: true,
                    hardRefreshOutcome: 'already_queued',
                });
                const message = 'Camera has already been queued for refresh, please wait...';
                this.streamStateManager.updateState({ info: message, error: '', freezed: false });
                setTimeout(() => {
                    const { info, error } = this.streamStateManager.getCurrentState();
                    if (info === message && !error) {
                        if (isStreaming) this.streamStateManager.setInfo('');
                        else this.streamStateManager.setError('Camera has already been refreshed, you can try again a bit later...');
                    }
                }, 3500);
            }
        } catch (err) {
            this.#recordTelemetry({
                eventType: 'hard_refresh_request_failed',
                hardRefreshAttempted: true,
                hardRefreshOutcome: 'request_failed',
                failureReason: err?.name || err?.code || 'hard_refresh_request_failed',
            });
            notificationHandlerSync({ err, title: `Error sending request for stream hard refresh, cameraId=${this.cameraId}` });
            this.streamStateManager.updateState({ info: '', error: '', freezed: false, isLoading: false });
        }
    }

    #addNewStream(camConfig) {
        try {
            const streamType = camConfig.videoData?.streamType;
            let cameraStreamPlayer = this.playerByStreamType(streamType);
            const cameraStreamConnection = this.#cameraStreamConnection;

            if (!cameraStreamConnection) this.#createCameraStreamConnection(camConfig);
            if (!cameraStreamPlayer) this.#createCameraStreamPlayer(camConfig);
        } catch (err) {
            this.#handle(err, '#addNewStream()');
        }
    }

    #createCameraStreamConnection(camConfig) {
        try {
            this.#log(`#createCameraStreamConnection(): creating new CameraStreamConnection`);
            const streamType = camConfig.videoData?.streamType;
            this.#cameraStreamConnection = new CameraStreamConnection(
                this.kurentoId,
                this.#setMediaStream,
                this.#reactivateStream,
                this.#updateStreamState.bind(this),
                camConfig,
                this.#getTelemetryContext,
                this.#getNackTarget
            );
            return (this.#cameraStreamConnection.activationPromise = this.#cameraStreamConnection.create(streamType));
        } catch (err) {
            this.#handle(err, '#createCameraStreamConnection()');
        }
    }

    #createCameraStreamPlayer(camConfig) {
        try {
            this.#log(`#createCameraStreamPlayer(): creating new CameraStreamPlayer`);
            const streamType = camConfig.videoData?.streamType;
            const cameraStreamPlayer = new CameraStreamPlayer(this.kurentoId, camConfig);
            this.#cameraStreamPlayerByStreamType.set(streamType, cameraStreamPlayer);
            return (cameraStreamPlayer.activationPromise = cameraStreamPlayer.activate());
        } catch (err) {
            this.#handle(err, '#createCameraStreamPlayer');
        }
    }

    async #updateStreamConfig(camConfig) {
        try {
            this.#updatingConfiguration = true;
            const {
                videoData: { playerEndpointId, streamType },
            } = camConfig;

            const mediaPipelineId = playerEndpointId ? playerEndpointId.split('/').at(0) : undefined;
            let cameraStreamPlayer = this.playerByStreamType(streamType);
            const cameraStreamConnection = this.#cameraStreamConnection;

            const promises = [];
            let oldWebRTCEndpoint;
            if (cameraStreamConnection) {
                try {
                    if (!cameraStreamConnection.live) await cameraStreamConnection.activationPromise;
                    oldWebRTCEndpoint = await cameraStreamConnection.getWebRTCEndpoint();
                    cameraStreamConnection.updateKurentoId(this.kurentoId);
                    promises.push(cameraStreamConnection.update(mediaPipelineId));
                } catch (err) {
                    this.#log(
                        `#updateStreamConfig(): CameraStreamConnection activationPromise rejected err=${err?.message || JSON.stringify(err)}, creating new CameraStreamConnection`
                    );
                    await this.#cameraStreamConnection.release();
                    promises.push(this.#createCameraStreamConnection(camConfig));
                }
            } else {
                promises.push(this.#createCameraStreamConnection(camConfig));
            }

            if (cameraStreamPlayer) {
                try {
                    if (!cameraStreamPlayer.live) await cameraStreamPlayer.activationPromise;
                    else if (this.activeStreamType === streamType && oldWebRTCEndpoint) {
                        this.#log(`#updateStreamConfig(): Update of active PlayerEndpoint in progress, disconnecting WebRTCEndpoint`);
                        await cameraStreamPlayer.disconnect(oldWebRTCEndpoint);
                    }
                    promises.push(cameraStreamPlayer.update(playerEndpointId, this.kurentoId));
                } catch (err) {
                    this.#log(
                        `#updateStreamConfig(): CameraStreamPlayer activationPromise rejected err=${err?.message || JSON.stringify(err)}, creating new CameraStreamPlayer`
                    );
                    promises.push(this.#createCameraStreamPlayer(camConfig));
                    this.#handle(err, '#updateStreamConfig()', false);
                }
            } else {
                promises.push(this.#createCameraStreamPlayer(camConfig));
            }

            await Promise.all(promises);

            this.#log(`#updateStreamConfig(): Updated CameraStreamConnection/Player config`);

            const streamTypeToActivate =
                this.#activateOnUpdateStreamType || this.activeStreamType || (this.#getVideoOutputIds().length && streamType) || '';

            this.#log(`#updateStreamConfig(): Initiating activation of streamType=${streamTypeToActivate}`);
            await this[ACTION_NAMES.ACTIVATE_CAMERA_STREAM_TYPE](streamTypeToActivate, 'updateConfig');

            // Reset only after the internal ACTIVATE completes — keeps any waiting ACTIVATE blocked
            // until the full update cycle (connection update + activation) is done
            this.#updatingConfiguration = false;
            this.#updatingConfigurationPromise = null;
            this.#activeMediaPiplineId = mediaPipelineId;
        } catch (err) {
            this.#updatingConfiguration = false;
            this.#updatingConfigurationPromise = null;
            this.#handle(err, '#updateStreamConfig()');
        }
    }

    #getPlayerToActivate(streamType, playerToActivate, connection) {
        try {
            let oppositeStreamType;

            if (!playerToActivate) {
                // try with opposite stream type
                oppositeStreamType = streamType === 'SUB' ? 'MAIN' : 'SUB';
                this.#log(`#getPlayerToActivate(): No player for StreamType=${streamType}, changing to streamType=${oppositeStreamType}`);
                playerToActivate = this.playerByStreamType(oppositeStreamType);
                if (!playerToActivate) {
                    this.#log(`#getPlayerToActivate():No player for StreamType=${oppositeStreamType} | exiting`);
                    this.activatingStreamType = '';
                    this.#setPlayerEndpointIdError(streamType, `[STREAM ERROR]: "No CameraStreamPlayer object"`);
                    throw new Error(`No CameraStreamPlayer object`);
                }
            }

            //check if playerEndpoint and webRtcEndpoint are in same mediaPipeline (problem may occur during hardrefresh)
            if (!playerToActivate.playerEndpointId.startsWith(connection?.mediaPipelineId)) {
                //if not try to connect to opposite streamType
                oppositeStreamType = streamType === 'SUB' ? 'MAIN' : 'SUB';
                this.#log(
                    `#getPlayerToActivate(): PlayerEndpoint and WebRTCEndpoint are not in same pipeline, changing streamType to ${oppositeStreamType}`
                );
                playerToActivate = this.playerByStreamType(oppositeStreamType);
                if (!playerToActivate || !playerToActivate?.playerEndpointId?.startsWith(connection?.mediaPipelineId)) {
                    this.activatingStreamType = '';
                    throw new Error(`Player and WebRTCEndpoint don't match. Hard refresh required!`);
                }
            }

            return { playerToActivate, oppositeStreamType };
        } catch (err) {
            this.#handle(err, '#getPlayerToActivate()');
        }
    }

    async #connectCameraStreamPlayerToConnection(currentPlayer, playerToActivate, connection, streamType) {
        try {
            const webRTCEndpoint = await connection.getWebRTCEndpoint();
            if (!webRTCEndpoint) throw new Error('No WebRTCEndpoint. Try hard refresh!');
            if (currentPlayer && currentPlayer.playerEndpointId.startsWith(connection.mediaPipelineId)) {
                await currentPlayer.disconnect(webRTCEndpoint);
            }
            await Promise.all([playerToActivate.connect(webRTCEndpoint), connection.configureWebRTCEndpointForStreamType(streamType)]);
        } catch (err) {
            this.#handle(err, '#connectCameraStreamPlayerToConnection()');
        }
    }

    #isCameraStreamConfigurationValid(camConfig) {
        const playerEndpointId = camConfig?.videoData?.playerEndpointId;
        const streamType = camConfig?.videoData?.streamType;
        const kurentoId = camConfig.kurentoId;
        const isPlayerIdChanged = playerEndpointId !== this.playerByStreamType(streamType)?.playerEndpointId;
        const isStreamConfigurationInvalidated = !!camConfig.invalidated;

        if (!playerEndpointId) {
            this.#setPlayerEndpointIdError(streamType, `[STREAM ERROR]: "PlayerEndpointId not provided"`);
            return handleInvalidConfiguration('PlayerEndpointId', playerEndpointId, this);
        } else if (this.#playerIdErrors.has(streamType)) {
            console.log(
                this.#fullHeader,
                `PlayerEndpointId received, delay=${Date.now() - this.#playerIdErrors.get(streamType)?.timestamp} miliseconds`
            );
            this.#playerIdErrors.delete(streamType);
        }
        if (!streamType) return handleInvalidConfiguration('StreamType', streamType, this);
        if (isPlayerIdChanged && !kurentoId) return handleInvalidConfiguration('KurentoId', kurentoId, this);

        this.#isStreamConfigurationInvalidated = isStreamConfigurationInvalidated;
        this.#cameraStreamConnection?.setIsInvalidated(isStreamConfigurationInvalidated);
        if (isStreamConfigurationInvalidated) this.#handleStreamConfigurationInvalidated('#isCameraStreamConfigurationValid');

        return !isStreamConfigurationInvalidated;
        function handleInvalidConfiguration(label, data, self) {
            self.#log(`#isCameraStreamConfigurationValid(): No ${label} provided, ${label}=${data} | exiting `);
            if (self?.activeStreamType || self?.activatingStreamType) return false;
            throw new Error(`${label} not provided`);
        }
    }

    #isCameraStreamConfigurationChanged(camConfig) {
        const { playerEndpointId: incomingPlayerEndpointId, streamType } = camConfig.videoData;
        const kurentoId = camConfig.kurentoId;
        const cameraStreamConnection = this.#cameraStreamConnection;
        const cameraStreamPlayer = this.playerByStreamType(streamType);
        const incomingMediaPipelineId = incomingPlayerEndpointId ? incomingPlayerEndpointId.split('/').at(0) : undefined;

        const isPlayerEndpointIdChanged = cameraStreamPlayer?.playerEndpointId !== incomingPlayerEndpointId;
        const isMediaPipelineIdChanged = cameraStreamConnection?.mediaPipelineId !== incomingMediaPipelineId;
        const isKurentoIdValid = this.#isKurentoIdValid(kurentoId);

        const isCameraStreamConfigChanged = isKurentoIdValid && (isPlayerEndpointIdChanged || isMediaPipelineIdChanged);
        if (isCameraStreamConfigChanged)
            this.#log(
                `#isCameraStreamConfigurationChanged(): New camera configuration received, kurentoId=${kurentoId} streamType=${streamType} playerEndpointId=${incomingPlayerEndpointId}`
            );
        else this.#log(`#isCameraStreamConfigurationChanged(): CameraStream configuration is unchanged`);

        return isCameraStreamConfigChanged;
    }

    #isKurentoIdValid(kurentoId) {
        const isUnchanged = kurentoId === this.kurentoId;
        if (isUnchanged) return true;
        if (this.#kurentoIdsCoolOffBench.has(kurentoId)) {
            this.#log(`#isCameraStreamConfigurationChanged(): Received kurentoId=${kurentoId} is on cool off bench, exiting`);
            return false;
        }
        const oldKurentoId = this.kurentoId;
        oldKurentoId && this.#kurentoIdsCoolOffBench.set(oldKurentoId, true);
        setTimeout(() => this.#kurentoIdsCoolOffBench.delete(oldKurentoId), this.#kurentoIdCoolOffMillis);
        this.#log(`#isCameraStreamConfigurationChanged(): Kurento id changed, old id=${this.kurentoId}, new id=${kurentoId}`);
        this.kurentoId = kurentoId;
        this.initiateKurentoClient(true);
        return true;
    }

    #shouldUpdateCameraStream(camConfig) {
        return this.#isCameraStreamConfigurationValid(camConfig) && this.#isCameraStreamConfigurationChanged(camConfig);
    }

    playerByStreamType(streamType) {
        return this.#cameraStreamPlayerByStreamType.get(streamType);
    }

    #setMediaStream = (mediaStream) => {
        this.#mediaStream = mediaStream;
        if (mediaStream) {
            // this is called once on CameraStreamConnection create/update
            this.schedule(ACTION_NAMES.EDIT_ENDPOINT_TAGS);
        }
        // this.#setMediaStreamOnVideoOutputs(mediaStream);
        this.#videoOutputManager.setMediaStreamOnVideoOutputs(mediaStream);
    };

    #enterFrozenState() {
        this.#log('Dispatching Freezed event to video element (immediate)');
        this.streamStateManager.updateState({ freezed: true });
    }

    #exitFrozenState() {
        this.streamStateManager.updateState({ freezed: false });
    }

    async [ACTION_NAMES.STOP_CAMERA_STREAM]() {
        if (this.aot) return;
        try {
            if (!this.#cameraStreamConnection) {
                this.#log(`${ACTION_NAMES.STOP_CAMERA_STREAM}(): No CameraStreamConnection, resetting activeStreamType`);
                this.activeStreamType = '';
                return;
            }
            this.streamStateManager.reset();
            this.streamStateManager.setInfo('Activating...');
            const currentPlayer = this.playerByStreamType(this.activeStreamType);
            const webRtcEndPoint = await this.#cameraStreamConnection.getWebRTCEndpoint();
            if (webRtcEndPoint) {
                currentPlayer && (await currentPlayer.disconnect(webRtcEndPoint));
                this.#cameraStreamConnection.addWebRtcEndpointTag('activeStream', 'IDLE');
                this.#log(`${ACTION_NAMES.STOP_CAMERA_STREAM}():paused cameraId=${this.cameraId}`);
            } else {
                this.#log(`${ACTION_NAMES.STOP_CAMERA_STREAM}(): No webrtcEndpoint`);
            }
            this.activeStreamType = '';
        } catch (err) {
            this.#handle(err, `${ACTION_NAMES.STOP_CAMERA_STREAM}()`, false);
        }
    }

    ///Events
    addEventListener(type, handler, videoOutputId = this.videoOutputId) {
        this.#videoOutputManager.addEventListener(type, handler, videoOutputId);
    }

    #updateStreamState(detail, videoOutputId) {
        this.streamStateManager.updateState(detail, videoOutputId);
    }

    addVideoOutput(videoOutputId, transactionId) {
        const isNewVideoOutput = this.#videoOutputManager.addVideoOutput(videoOutputId, transactionId);
        if (isNewVideoOutput) {
            this.#setLogHeaders();
            this.schedule(ACTION_NAMES.EDIT_ENDPOINT_TAGS);
        }
        this.streamStateManager.updateState(this.streamStateManager.getCurrentState(), videoOutputId);

        if (this.#mediaStream) this.#videoOutputManager.setMediaStreamOnVideoOutput(videoOutputId, this.#mediaStream);
        else
            this.#cameraStreamConnection?.mediaStreamPromise
                ?.then((mediaStream) => this.#videoOutputManager.setMediaStreamOnVideoOutput(videoOutputId, mediaStream))
                .catch((err) => this.#handle(err, 'addVideoOutput()', false));
        if (this.#isStreamConfigurationInvalidated) {
            this.#handleStreamConfigurationInvalidated('addVideoOutput');
            return;
        }
        this.schedule(ACTION_NAMES.EDIT_ENDPOINT_TAGS);
    }

    removeVideoOutput(videoOutputId) {
        try {
            this.#videoOutputManager.removeVideoOutput(videoOutputId);
            this.#setLogHeaders();
            if (this.#isStreamConfigurationInvalidated) {
                this.#handleStreamConfigurationInvalidated('removeVideoOutput');
                return;
            }
            if (!this.#videoOutputManager.hasVideoOutputs()) this.schedule(ACTION_NAMES.STOP_CAMERA_STREAM);
            this.schedule(ACTION_NAMES.EDIT_ENDPOINT_TAGS);
        } catch (err) {
            this.#handle(err, 'removeVideoOutput', false);
        }
    }

    makeExclusiveForVideoOutputs(videoOutputIds) {
        this.#videoOutputManager.makeExclusiveForVideoOutputs(videoOutputIds);
    }

    #getVideoOutputIds() {
        return this.#videoOutputManager.getVideoOutputIds();
    }

    getTransactionIds() {
        return this.#videoOutputManager.getTransactionIds();
    }

    isActiveOnVideoWall() {
        return this.#videoOutputManager.isActiveOnVideoWall(videoWallTransactioId);
    }

    #getTransactionTagValue() {
        return this.#videoOutputManager.getTransactionTagValue();
    }

    #setLogHeaders() {
        const videoOutputIdsString = this.#getVideoOutputIds().join(', ');
        this.#fullHeader = `[KurentoRtspSource kurentoId=${this.kurentoId}, cameraId=${this.cameraId}, ${
            videoOutputIdsString ? `videoOutputIds=[${videoOutputIdsString}], ` : ''
        }transactionId=${this.#getTransactionTagValue()}]`;
    }

    #getTelemetryContext = () => {
        const camConfig = this.#latestCamConfig || {};
        return {
            streamType: this.activeStreamType || this.activatingStreamType || camConfig.videoData?.streamType,
            site: camConfig.locationName,
            cameraId: this.cameraId,
            cameraName: camConfig.generatedName || camConfig.name,
            kurentoId: this.kurentoId,
            transactionId: this.#getTransactionTagValue(),
        };
    };

    #getNackTarget = () => {
        const camConfig = this.#latestCamConfig || {};
        return {
            locationName: camConfig.locationName,
            gateId: camConfig.gateId,
            cameraType: camConfig.type,
            cameraId: this.cameraId,
        };
    };

    #recordTelemetry(event, { peerConnection = null, immediate = false } = {}) {
        webRtcTelemetry.recordPeerConnectionEvent(
            {
                ...this.#getTelemetryContext(),
                ...event,
            },
            peerConnection,
            { immediate }
        );
    }

    hasVideoOutputId(videoOutputId) {
        return this.#videoOutputManager.hasVideoOutputId(videoOutputId);
    }

    isExclusivelyUsedForTransactionId(transactionId) {
        return this.#videoOutputManager.isExclusivelyUsedForTransactionId(transactionId);
    }

    audio(enabled) {
        this.streamStateManager.setAudioEnabled(enabled);
    }

    get streamState() {
        return this.streamStateManager.getCurrentState();
    }

    async [ACTION_NAMES.EDIT_ENDPOINT_TAGS]() {
        try {
            const cameraStreamConnection = this.#cameraStreamConnection;
            cameraStreamConnection && (await cameraStreamConnection.addWebRtcEndpointTag('transactionId', `${this.#getTransactionTagValue()}`));
        } catch (err) {
            this.#handle(err, `${ACTION_NAMES.EDIT_ENDPOINT_TAGS}()`);
        }
    }

    getInfo() {
        const activeStreamType = this.activeStreamType || 'not available';
        const activePlayer = activeStreamType ? this.#cameraStreamPlayerByStreamType.get(activeStreamType) : null;

        const url = activePlayer?.maskedUrl || 'not available';
        const mediaPipelineId = this.#cameraStreamConnection?.mediaPipelineId || 'not available';
        const playerEndpointId = activePlayer?.playerEndpointId?.split('/')?.at(1) || 'not available';
        const webRTCEndpointId = this.#cameraStreamConnection?.webRTCEndpoint?.id?.split('/')?.at(1) || 'not available';

        let info = `<strong>CameraId: </strong> ${this.cameraId}`;
        info += `&nbsp;&nbsp;<strong>StreamType:</strong> ${activeStreamType}<br/><br/>`;
        info += `<strong>URL: </strong> ${url} <br/><br/>`;
        info += `<strong>KurentoID: </strong> ${this.kurentoId} <br/><br/>`;
        info += `<strong>MediaPipeline:</strong> ${mediaPipelineId}<br/><br/>`;
        info += `<strong>PlayerEndpoint: </strong> ${playerEndpointId}<br/><br/>`;
        info += `<strong>WebRTCEndpoint: </strong> ${webRTCEndpointId}<br/><br/>`;

        info += '<strong>Media:</strong>&nbsp;';

        const mediaStream = this.#mediaStream;
        if (!mediaStream) {
            info += 'not available';
        } else {
            const [videoTrack] = mediaStream.getVideoTracks();
            const videoTrackSettings = videoTrack.getSettings();
            info += `\n[id=${videoTrack.id},\n`;
            info += `\treadyState=${videoTrack.readyState}, frameRate=${videoTrackSettings.frameRate},\n`;
            info += `\tfacingMode=${videoTrackSettings.facingMode}, h=${videoTrackSettings.height}, w=${videoTrackSettings.width}]\n`;

            const [audioTrack] = mediaStream.getAudioTracks();
            if (!audioTrack) return info;

            info += '<br/><br/><strong>Audio:</strong>';
            const audioTrackSettings = audioTrack.getSettings();
            info += `\n[id=${audioTrack.id},\n`;
            info += `\treadyState=${audioTrack.readyState}, volume=${audioTrackSettings.volume},\n`;
            info += `\tchannelCount=${audioTrackSettings.channelCount}, sampleRate=${audioTrackSettings.sampleRate}]`;
        }

        return info;
    }

    get webRtcEndpointId() {
        return this.#cameraStreamConnection?.webRTCEndpoint?.id;
    }

    get localICECandidatesUsed() {
        return this.#cameraStreamConnection?.localIceCandidatesUsed;
    }

    #log(...args) {
        // Check if last argument is a valid console method (mode)
        const validModes = ['log', 'error', 'warn', 'info', 'debug'];
        const lastArg = args[args.length - 1];
        const mode = typeof lastArg === 'string' && validModes.includes(lastArg) ? args.pop() : 'log';
        if (!isCameraStreamLogsEnabled(mode)) return;

        // Serialize objects for Grafana compatibility
        const serializedArgs = args.map((arg) => {
            if (typeof arg === 'object' && arg !== null) {
                try {
                    return JSON.stringify(arg);
                    // eslint-disable-next-line
                } catch (_) {
                    return '[Object - serialization failed]';
                }
            }
            return arg;
        });

        console[mode](`${this.#fullHeader}`, ...serializedArgs);
    }

    #logError(...args) {
        if (this.#isPlayerEndpointIdError(...args)) {
            if (this.#playerEndpointIdErrorTimeoutId) return;
            this.#playerEndpointIdErrorTimeoutId = setTimeout(() => this.#logPlayerEndpointsErrors(), 10000);
            return;
        }
        console.error(`${this.#fullHeader}`, ...args);
    }

    #isPlayerEndpointIdError(error) {
        return error === `[STREAM ERROR]: "PlayerEndpointId not provided"` || error === `[STREAM ERROR]: "No CameraStreamPlayer object"`;
    }

    #logPlayerEndpointsErrors() {
        const errors = this.#getPlayerEndpointIdErrors() || [];
        errors.forEach((err) => console.error(`${this.#fullHeader}`, err));
        this.#playerIdErrors = new Map();
        this.#playerEndpointIdErrorTimeoutId = null;
    }
    #setPlayerEndpointIdError(streamType, error) {
        if (this.#playerIdErrors.has(streamType)) {
            this.#playerIdErrors.get(streamType)?.errors?.push?.(error);
        } else {
            this.#playerIdErrors.set(streamType, { timestamp: Date.now(), errors: [error] });
        }
    }
    #getPlayerEndpointIdErrors() {
        return [...(this.#playerIdErrors.get(STREAM_TYPE.MAIN)?.errors || []), ...(this.#playerIdErrors.get(STREAM_TYPE.SUB)?.errors || [])];
    }

    //ERROR HANDLERS

    #handle(err, message, rethrow = true) {
        if (err) {
            this.#log(`${message}: ${err?.message || err}`, 'error');
            this.#handlePlaybackError(err, message);
            if (this.#forceStop) {
                this.#forceStop = false;
                this[ACTION_NAMES.RELEASE_CAMERA_STREAM]();
                return;
            }
            if (rethrow) throw new Error(err?.message || err);
        }
    }

    #handlePlaybackError(err) {
        this.streamStateManager.updateState({ info: '', error: err.message, isLoading: false });
    }

    #handleStreamConfigurationInvalidated(methodName) {
        this.#log(`${methodName}(): Stream configuration invalidated, exiting`);
        this.streamStateManager.setError(`Stream configuration invalidated, please try hard refresh!`);
    }

    // String representation

    toString() {
        return this.#fullHeader;
    }
}
